/**
 * APP-6D symbol id codes for the tactical graphics this client can draw, plus
 * the affiliation rules that decide which variant of a code a drawn graphic
 * actually gets.
 *
 * Kept in a dependency-free module of its own so non-map code (the Redux UI
 * slice, services, tests) can reference a SIDC without pulling in Leaflet or the
 * renderer. The symbol classes in this directory import from here too, so there
 * is exactly one spelling of each code.
 *
 * The affiliation logic itself lives in `shared/symbology.js`, copied verbatim
 * from the umbrella repo and shared with the Android WebView — the two clients
 * must agree on what a red line means. This module only puts a typed face on it.
 */

import symbology from './shared/symbology';

/** "Axis of Advance – Main Attack (Friendly Ground)".
 *  30-digit symbol ID: ver 10 · reality · friend · symbol set 25 (control
 *  measure) · entity 151403 (Main Attack). The wide "main thrust" arrow.
 *
 *  This is the *base* code: the friendly, present variant. A graphic drawn from
 *  a parent takes the same code with its identity (and, for an enemy, its
 *  status) rewritten — see {@link graphicSidcForParent}. */
export const SIDC_AXIS_OF_ADVANCE = '100325000015140300000000000000';

/** Affiliation bucket a graphic is drawn as. */
export type Affiliation = 'friend' | 'hostile' | 'neutral' | 'unknown';

/**
 * The SIDC a graphic drawn from `parentSidc` should carry: the parent's
 * affiliation, and Planned/Anticipated status when that parent is an enemy.
 *
 * `parentSidc` is null/undefined for a standalone graphic, which keeps
 * `baseSidc` as chosen in the toolbox.
 */
export function graphicSidcForParent(
    baseSidc: string,
    parentSidc: string | null | undefined,
): string {
    return symbology.forParent(baseSidc, parentSidc);
}

/** Affiliation a SIDC denotes; `unknown` when it cannot be read. */
export function affiliationOf(sidc: string | null | undefined): Affiliation {
    return symbology.affiliationOf(sidc);
}

/**
 * Stroke/fill colour for a graphic with this SIDC — friendly black, enemy red,
 * neutral green, unknown yellow.
 */
export function graphicColor(sidc: string | null | undefined): string {
    return symbology.colorOf(sidc);
}

/**
 * Reduce a SIDC to the code identifying its *symbol family*, by resetting the
 * two digits a drawn graphic varies: identity and status.
 *
 * The symbol registry keys off this, so a hostile planned axis of advance and a
 * friendly present one resolve to the same drawing code rather than needing an
 * entry per affiliation (7 identities x 2 statuses per family).
 */
export function sidcFamily(sidc: string): string {
    return symbology.withStatus(
        symbology.withIdentity(sidc, symbology.IDENTITY_FRIEND),
        symbology.STATUS_PRESENT,
    );
}
